import Graph from "./graph";
import Node from "./node";
import Link from "./link";
import { distanceType } from "./types";

export default class DijkstraSearch {
  static get BY_LINK_LENGTH(): distanceType {
    return "BY_LINK_LENGTH" as distanceType;
  }

  static get BY_WEIGHT(): distanceType {
    return "BY_WEIGHT" as distanceType;
  }

  static get BY_COORDINATES(): distanceType {
    return "BY_COORDINATES" as distanceType;
  }

  static search(
    start: string,
    finish: string,
    graph: Graph,
    distanceType: distanceType
  ): Node[] {
    if (!graph.nodes[start] || !graph.nodes[finish]) {
      console.error("Nodes are not part of the graph!");
      return [];
    }

    const beginning = graph.nodes[start];
    const end = graph.nodes[finish];

    const queue: Node[] = [];
    queue.push(beginning);

    const tested: (Node | undefined)[] = [];
    const nodesProps: any = Object.keys(graph.nodes).reduce(
      (reduced, name) => ({
        ...reduced,
        [name]: { distanceFromStart: Infinity },
      }),
      {}
    );
    nodesProps[start] = { distanceFromStart: 0 };

    while (queue.length > 0) {
      queue.sort(
        (a, b) =>
          nodesProps[a.name].distanceFromStart -
          nodesProps[b.name].distanceFromStart
      );

      const current = queue.shift();
      tested.push(current);

      current &&
        current.paths.forEach((link) => {
          const endPoint = graph.nodes[link.toNodeName];
          if (tested.includes(endPoint)) return;

          const currentDistance = nodesProps[current.name].distanceFromStart;
          const newDistance =
            currentDistance +
            DijkstraSearch.getDistance(current, link, endPoint, distanceType);

          if (nodesProps[endPoint.name].distanceFromStart > newDistance) {
            nodesProps[endPoint.name] = {
              prev: current,
              distanceFromStart: newDistance,
            };
          }

          const index = queue.indexOf(endPoint);
          if (index !== -1) {
            queue[index] = endPoint;
          } else {
            queue.push(endPoint);
          }
        });
    }

    if (nodesProps[finish].distanceFromStart !== Infinity) {
      const path = [];
      let last = end;
      while (last.name !== start) {
        path.unshift(last);
        last = nodesProps[last.name].prev;
      }
      path.unshift(beginning);
      return path;
    }

    return [];
  }

  static getDistance(
    node: Node,
    link: Link,
    endPoint: Node,
    distanceType: distanceType
  ): number {
    if (distanceType === DijkstraSearch.BY_LINK_LENGTH) {
      return link.length;
    }

    if (distanceType === DijkstraSearch.BY_COORDINATES) {
      const a = node;
      const b = endPoint;
      return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
    }

    if (distanceType === DijkstraSearch.BY_WEIGHT) {
      return endPoint.weight;
    }

    return 0;
  }
}
